<template>
  <b-card
    header-class="p-0"
    class="page-card"
    :class="{ 'token-invalid': tokenInvalid }"
    no-body
  >
    <div
      v-if="tokenInvalid"
      class="token-renew-overlay"
      role="button"
      tabindex="0"
      @click="renewToken"
      @keydown.enter="renewToken"
    >
      <div class="token-renew-content">
        <p class="mb-50 font-weight-bold">
          {{ $t('dashboard.token_needs_renew') }}
        </p>
        <span class="token-renew-link">{{ $t('dashboard.token_renew_click') }}</span>
      </div>
    </div>
    <template #header>
      <div
        class="mb-0 image-container"
        :style="{backgroundImage:`url(${page.cover})`,height:'150px',width:'100%'}"
      >
        <div
          class="ribbon ribbon-small"
          :class="page.expired?'ribbon-red':'ribbon-success'"
        >
          <div class="banner">
            <div class="text">
              {{ getPageTrans(page.expired?'expired':'active') }}
            </div>
          </div>
        </div>
        <div class="page-cover-overlay" />
        <div class="page-name">
          <a
            class="h3"
            :href="`https://facebook.com/${page.page_id}`"
            target="_blank"
          >{{ page.name }}</a>
          <h6 class="mb-0 mt-1">
            {{ $t('page.'+(page.expired?'expired-at':'should_renew_at'),{date:page.expired_date}) }}
          </h6>
        </div>
        <div class="profile-image-wrapper">
          <div class="profile-image p-0">
            <b-avatar
              size="90"
              variant="primary"
              :src="page.profile"
            />
          </div>
        </div>
      </div>
    </template>
    <b-card-body>
      <ul class="page-features">
        <feature-row
          :enabled="page.has_reply"
          :page-id="page.id"
          title="reply.menu-title"
          route="owner:posts"
        >
          <b-button
            size="sm"
            variant="outline-primary"
            @click="openPage('owner:posts')"
          >
            {{ $t('reply.enable') }}
          </b-button>
        </feature-row>
        <feature-row
          :enabled="page.has_poster || page.has_reply"
          :page-id="page.id"
          title="menu.posting_service"
          route="owner:poster"
        >
          <b-button
            size="sm"
            variant="outline-primary"
            @click="openPage('owner:poster')"
          >
            {{ $t('poster.post') }}
          </b-button>
        </feature-row>
        <feature-row
          :enabled="page.has_bot"
          :page-id="page.id"
          title="bot.title"
          route="owner:webview-requests"
        >
          <b-button
            size="sm"
            :disabled="submitting"
            :variant="`${(currentPage.bot_enabled)?'outline-danger':'outline-primary'}`"
            @click="enableDisableBot()"
          >
            <b-spinner
              v-if="submitting"
              small
              class="mr-50"
            />
            {{ $t(`bot.${(currentPage.bot_enabled)?'disable-bot':'enable-bot'}`) }}
          </b-button>
        </feature-row>
      </ul>
    </b-card-body>
    <b-card-footer>
      <div class="d-flex justify-content-center text-dark flex-wrap gap-2">
        <!--<div>
         <span style="font-weight: 600">{{ getPageTrans('package-name') }}</span>
         {{ page.package.name }}
       </div>
      <div>
         <span style="font-weight: 600">{{ getPageTrans('package-price') }}</span>
         {{ page.package.price }}
       </div>-->
        <b-button
          size="sm"
          variant="outline-danger"
          @click="deletePage"
        >
          <FeatherIcon
            icon="TrashIcon"
          />
          {{ $t('page.delete') }}
        </b-button>
      </div>
    </b-card-footer>
  </b-card>
</template>

<script>
import {
  BCard, BAvatar, BCardFooter, BCardBody, BButton,
} from 'bootstrap-vue'
import FeatureRow from '@/views/owner-fb/page/components/FeatureRow'
import PageApi from '@/api/page'
import ChatBotApi from '@/api/bot'
import AuthMixin from '@/views/auth/mixins/auth-mixin'

export default {
  name: 'PageCard',
  components: {
    FeatureRow,
    BCard,
    BAvatar,
    BButton,
    BCardFooter,
    BCardBody,
  },
  mixins: [AuthMixin],
  props: {
    page: {
      type: Object,
      default: () => {},
    },
    tokenInvalid: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      submitting: false,
      currentPage: {},
    }
  },
  computed: {
    hasInsta() {
      return this.currentPage?.insta_account && this.currentPage.insta_account?.id
    },
  },
  mounted() {
    this.currentPage = { ...this.page }
  },
  methods: {
    renewToken() {
      this.authFacebook(false)
    },
    async openPage(route, insta = false) {
      if (insta) {
        await this.$store.dispatch('instagram/SET_ACCOUNT', this.page.insta_account.id)
        await this.$store.commit('auth/SET_MODEL_TYPE', 'instagram')
      } else {
        await this.$store.dispatch('pages/SET_PAGE', this.page.id)
        await this.$store.commit('auth/SET_MODEL_TYPE', 'fb')
      }
      this.$router.push({ name: route })
    },
    getPageTrans(str) {
      return this.$t(`page.${str}`)
    },
    deletePage() {
      this.$okConfirm(this.$t('general.delete-confirm'), {
        preConfirm: () => PageApi.deletePageByOwner(this.page.id)
          .then(response => {
            const pages = this.$store.getters['pages/pages'] ?? []
            const pageIndex = pages.findIndex(page => page.id === this.page.id)
            if (pageIndex > -1) {
              pages.splice(pageIndex, 1)
              this.$store.commit('pages/SET_PAGES', pages)
            }
            this.$notifySuccess(response.message)
          })
          .catch(e => this.$notifyError(e)),
      })
    },
    async enableDisableBot() {
      const label = this.currentPage.bot_enabled ? 'disable-bot-msg' : 'enable-bot-msg'
      this.$okConfirm(this.$t(`bot.${label}`), {
        confirmButtonText: this.$t('forms.submit'),
        showCancel: true,
        preConfirm: () => ChatBotApi.enableDisableBot(this.page.id).then(res => {
          if (res.error === 0) {
            this.currentPage.bot_enabled = !this.currentPage.bot_enabled
            this.$store.dispatch('pages/UPDATE_PAGE', this.currentPage)
            this.$notifySuccess(res.message)
          } else {
            this.$notifyError(res.message)
          }
        }).catch(e => this.$notifyError(e))
          .finally(() => {
            this.submitting = false
          }),
      })
    },
  },

}
</script>
